import { userStore } from "@sourcewhale/shared/src/svelte/api-stores/user-store";
import { derived } from "svelte/store";
import { notetakerRelease } from "../../../pages/Meetings/utils";
import { diallerRequestedStore } from "./dialler-store";
import { todosCountStore } from "./todos-count-store";

const baseNavItems = [
  {
    id: "dashboard",
    name: "Dashboard",
    icon: "fa-regular fa-grid-2",
    activeIcon: "fa-solid fa-grid-2",
    link: "/dashboard",
    divider: true,
    display: true,
  },
  {
    id: "campaigns",
    name: "Campaigns",
    icon: "fa-regular fa-megaphone",
    activeIcon: "fa-solid fa-megaphone",
    link: "/campaigns",
    divider: false,
    display: true,
  },
  {
    id: "todos",
    name: "To-Dos",
    icon: "fa-regular fa-check-square",
    activeIcon: "fa-solid fa-check-square",
    link: "/todo",
    divider: false,
    display: true,
  },
  {
    id: "outbox",
    name: "Outbox",
    icon: "fa-regular fa-box-archive",
    activeIcon: "fa-solid fa-box-archive",
    link: "/outbox",
    divider: true,
    display: true,
  },
  {
    id: "calls",
    name: "Calls",
    icon: "fa-regular fa-phone",
    activeIcon: "fa-solid fa-phone",
    link: "/calls",
    divider: false,
    display: true,
  },
  {
    id: "meetings",
    name: "Meetings",
    icon: "fa-regular fa-video",
    activeIcon: "fa-solid fa-video",
    link: "/meetings",
    divider: true,
    display: true,
  },
  {
    id: "people",
    name: "People",
    icon: "fa-regular fa-user-group",
    activeIcon: "fa-solid fa-user-group",
    menu: [
      {
        name: "Contacts",
        link: "/contacts",
      },
      {
        name: "Upload",
        link: "/upload",
      },
    ],
    divider: false,
    display: true,
  },
  {
    id: "projects",
    name: "Projects",
    icon: "fa-regular fa-folder-user fa-fw",
    activeIcon: "fa-solid fa-folder-user fa-fw",
    link: "/projects",
    divider: false,
    display: true,
  },
  {
    id: "companies",
    name: "Companies",
    icon: "fa-regular fa-building fa-fw",
    activeIcon: "fa-solid fa-building fa-fw",
    link: "/companies",
    divider: false,
    display: true,
  },
  {
    id: "search",
    name: "Search",
    icon: "fa-regular fa-magnifying-glass fa-fw",
    activeIcon: "fa-solid fa-magnifying-glass fa-fw",
    link: "/search", // TODO: remove legacy /search-builder page
    divider: true,
    display: true,
  },
  {
    id: "reports",
    name: "Reports",
    icon: "fa-regular fa-chart-pie",
    activeIcon: "fa-solid fa-chart-pie",
    link: "/reports",
    divider: false,
    display: true,
  },
];

const meetingsBadge = (user) => {
  if (user.notetakerPlan === "trial") {
    return {
      badgeValue: "TRIAL",
      badgeClass: "trial",
    };
  }
  if (["trialEnded", "standard", "admin"].includes(user.notetakerPlan)) {
    return {
      badgeValue: null,
      badgeClass: null,
    };
  }
  // Set the expiry date to 30 days from user creation date or notetaker release
  // date, whichever is later
  const newExpiry =
    Math.max(user.creationTime, notetakerRelease) + 30 * 24 * 60 * 60;
  const now = Math.floor(Date.now() / 1000);
  return { badgeValue: now < newExpiry ? "NEW" : null, badgeClass: "new" };
};

const projectsBadge = (user) =>
  user.hasCrm ? { badgeClass: "new", badgeValue: "NEW" } : {};

const searchBadge = () => {
  const now = Math.floor(Date.now() / 1000);
  const oneMonth = 30 * 24 * 60 * 60;
  const searchRelease = 1763510400; // 2025-11-19
  return searchRelease <= now && now < searchRelease + oneMonth
    ? { badgeValue: "NEW", badgeClass: "new" }
    : {};
};

export const navItemsStore = derived(
  [userStore, todosCountStore, diallerRequestedStore],
  ([$userStore, $todosCountStore, $diallerRequestedStore]) =>
    baseNavItems
      .map((item) => {
        if (item.id === "companies" && !$userStore.hasCrm) {
          return {
            ...item,
            display: false,
          };
        }
        if (item.id === "calls") {
          const { hasUsedDialler, isPlgTrial } = $userStore;
          return {
            ...item,
            badgeValue:
              isPlgTrial || hasUsedDialler || $diallerRequestedStore
                ? null
                : "NEW",
            badgeClass: "new",
          };
        }
        if (item.id === "meetings") {
          return {
            ...item,
            ...meetingsBadge($userStore),
          };
        }
        if (
          item.id === "todos" &&
          $todosCountStore &&
          $todosCountStore !== "0"
        ) {
          return {
            ...item,
            badgeValue: $todosCountStore,
            badgeClass: "yellow",
          };
        }
        if (item.id === "people" && $userStore.isPlgTrial) {
          return {
            ...item,
            link: "/people",
            display: true,
            menu: null,
            divider: false,
          };
        }
        if (item.id === "projects") {
          if ($userStore.disableProjects) {
            return {
              ...item,
              disabled: true,
            };
          }
          return {
            ...item,
            ...projectsBadge($userStore),
          };
        }
        if (item.id === "search") {
          return {
            ...item,
            name: $userStore.isPlgTrial ? "AI Search" : "Search",
            ...searchBadge(),
          };
        }
        return item;
      })
      .filter((item) => item.display),
);
